import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Crown, Zap, LogOut, ChevronDown, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ProBadge, TrialBadge } from './UpgradeGate';

export default function Topbar({ title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const trial = user?.trial || {};
  const inTrial = trial.in_trial;
  const expired = trial.expired;

  return (
    <div className="flex justify-between items-center mb-6 gap-3">
      <h1 className="text-[25px] font-bold tracking-tight m-0">{title}</h1>
      <div className="flex items-center gap-2">
        <span
          className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
          style={{ background: 'var(--lf-green-bg)', color: 'var(--lf-green)' }}
        >
          <CheckCircle2 size={13} />
          AI Engine: Ready
        </span>
        {user && !user.is_pro && (
          <button
            onClick={() => navigate('/upgrade')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{
              background: expired ? '#dc2626' : 'var(--lf-navy)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Zap size={12} /> {expired ? 'Trial ended · Upgrade' : 'Upgrade'}
          </button>
        )}
        {user && (
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center gap-2 p-1 pr-2 rounded-full"
              style={{
                background: '#ffffff',
                border: '1px solid var(--lf-line)',
                cursor: 'pointer',
              }}
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-7 h-7 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: 'var(--lf-blue)', color: '#fff' }}
                >
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <span className="hidden sm:inline text-xs font-semibold" style={{ color: '#111827' }}>
                {user.name?.split(' ')[0]}
              </span>
              {user.is_pro ? (
                <ProBadge />
              ) : inTrial ? (
                <TrialBadge daysRemaining={trial.days_remaining} />
              ) : null}
              <ChevronDown size={13} style={{ color: 'var(--lf-muted)' }} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-1 bg-white rounded-xl p-2 z-20"
                style={{
                  minWidth: 220,
                  border: '1px solid var(--lf-line)',
                  boxShadow: '0 20px 40px rgba(15,23,42,.1)',
                }}
              >
                <div className="px-2 py-1.5">
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs" style={{ color: 'var(--lf-muted)' }}>
                    {user.email}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {user.is_pro ? (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{
                          background: 'linear-gradient(90deg,#f59e0b,#f97316)',
                          color: '#fff',
                        }}
                      >
                        <Crown size={9} /> PRO
                      </span>
                    ) : inTrial ? (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: '#dbeafe', color: '#1e40af' }}
                      >
                        <Clock size={9} /> TRIAL · {trial.days_remaining}d left
                      </span>
                    ) : expired ? (
                      <span
                        className="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: '#fee2e2', color: '#991b1b' }}
                      >
                        TRIAL ENDED
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: '#eef2f7', color: '#344054' }}
                      >
                        FREE
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ height: 1, background: 'var(--lf-line)', margin: '6px 0' }} />
                {!user.is_pro && (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/upgrade');
                    }}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-sm flex items-center gap-2"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#111827' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f8faff')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Zap size={13} style={{ color: 'var(--lf-blue)' }} /> Upgrade to Pro
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-2 py-1.5 rounded-lg text-sm flex items-center gap-2"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#111827' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f8faff')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <LogOut size={13} /> Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
